import React, { useState, useEffect, useRef } from 'react';
import Input from '../../../../../../components/Input/Input';
import './ChatHistoryGroup.css';
import { groupService } from '../../../../../../services/Group';
import { formatDateTime } from '../../../../../../utils/utils';
import searchIcon from '../../../../../../assets/header/searchIcon.png';
import userIcon from '../../../../../../assets/menu/menu_user.png';

const ChatHistoryGroup = ({ groupId }) => {
  const [allMessages, setAllMessages] = useState([]);
  const [visibleMessages, setVisibleMessages] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectedMessageId, setSelectedMessageId] = useState(null);
  const [messagesPerLoad] = useState(5);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [viewType, setViewType] = useState('chat');
  const [multimediaFiles, setMultimediaFiles] = useState([]);
  const [participantRoles, setParticipantRoles] = useState({});
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const chatContainerRef = useRef(null);
  const messageRefs = useRef({});
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (groupId) {
      console.log('Fetching messages for groupId:', groupId);
      fetchMessages();
    }
  }, [groupId]);

  useEffect(() => {
    console.log('All messages:', allMessages.length, 'Visible messages:', visibleMessages.length);
  }, [allMessages, visibleMessages]);

  useEffect(() => {
    if (searchTerm.trim()) {
      performSearch();
    } else {
      setSearchResults([]);
    }
  }, [searchTerm, allMessages]);

  useEffect(() => {
    const handleScroll = (e) => {
      const container = e.target;
      if (!container || loadingMore || !hasMoreMessages) return;
      
      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      
      if (distanceFromBottom < 50 && !loadingMore && hasMoreMessages) {
        loadMoreMessages();
      }
    };

    const container = chatContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll, { passive: true });
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [loadingMore, hasMoreMessages, visibleMessages.length, allMessages.length]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await groupService.getMessagesByGroup(groupId);
      console.log('Fetched messages response:', response);
      if (response && response.messages && response.messages.length > 0) {
        const rolesMap = {};
        if (response.participants && response.participants.length > 0) {
          response.participants.forEach(participant => {
            rolesMap[participant.user_id] = participant.role || participant.role_name || 'Member';
          });
        }
        setParticipantRoles(rolesMap);
        
        setAllMessages(response.messages);
        const initialMessages = response.messages.slice(0, messagesPerLoad);
        setVisibleMessages(initialMessages);
        setHasMoreMessages(response.messages.length > messagesPerLoad);
        
        const multimedia = response.messages.filter(msg => msg.file_url && msg.file_type).map(msg => ({
          id: msg.id,
          file_url: msg.file_url,
          file_type: msg.file_type,
          file_name: msg.file_name,
          sender_name: msg.sender_name,
          sender_id: msg.sender_id,
          created_at: msg.created_at,
          message: msg.message
        }));
        setMultimediaFiles(multimedia);
      } else {
        setAllMessages([]);
        setVisibleMessages([]);
        setMultimediaFiles([]);
        setHasMoreMessages(false);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
      setAllMessages([]);
      setVisibleMessages([]);
      setMultimediaFiles([]);
      setHasMoreMessages(false);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreMessages = () => {
    if (loadingMore || !hasMoreMessages) return;
    
    setLoadingMore(true);
    
    requestAnimationFrame(() => {
      const currentCount = visibleMessages.length;
      const nextBatch = allMessages.slice(currentCount, currentCount + messagesPerLoad);
      
      if (nextBatch.length > 0) {
        setVisibleMessages(prev => [...prev, ...nextBatch]);
        const newTotal = currentCount + nextBatch.length;
        setHasMoreMessages(newTotal < allMessages.length);
      } else {
        setHasMoreMessages(false);
      }
      
      setLoadingMore(false);
    });
  };

  const performSearch = () => {
    const results = allMessages.filter(message => 
      message.message && 
      message.message.toLowerCase().includes(searchTerm.toLowerCase())
    ).map((message) => {
      const messageIndex = allMessages.findIndex(msg => msg.id === message.id);
      
      return {
        ...message,
        highlightedContent: highlightSearchTerm(message.message, searchTerm),
        messageIndex: messageIndex
      };
    });
    
    setSearchResults(results);
  };

  const highlightSearchTerm = (text, term) => {
    if (!term.trim()) return text;
    
    const regex = new RegExp(`(${term})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? 
        `<mark key=${index}>${part}</mark>` : 
        part
    ).join('');
  };

  const scrollToMessage = (messageId) => {
    const messageIndex = allMessages.findIndex(msg => msg.id === messageId);
    if (messageIndex !== -1) {
      const requiredMessages = messageIndex + 1;
      const currentVisibleCount = visibleMessages.length;
      
      if (requiredMessages > currentVisibleCount) {
        const messagesToLoad = allMessages.slice(0, requiredMessages);
        setVisibleMessages(messagesToLoad);
        setHasMoreMessages(requiredMessages < allMessages.length);
      }
      
      setTimeout(() => {
        setSelectedMessageId(messageId);
        const messageElement = messageRefs.current[messageId];
        
        if (messageElement && chatContainerRef.current) {
          messageElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
          
          messageElement.classList.add('highlighted-message');
          setTimeout(() => {
            messageElement.classList.remove('highlighted-message');
            setSelectedMessageId(null);
          }, 3000);
        }
      }, 200);
    }
  };

  const formatMessageTime = (timestamp) => {
    return formatDateTime(timestamp);
  };

  const getFileTypeWithExtension = (fileType, fileName, fileUrl) => {
    const source = fileName || fileUrl || '';
    const extensionMatch = source.match(/\.([^.]+)$/);
    const extension = extensionMatch ? extensionMatch[1].toLowerCase() : '';
    const capitalizedType = fileType ? fileType.charAt(0).toUpperCase() + fileType.slice(1) : 'File';
    return extension ? `${capitalizedType} - .${extension}` : capitalizedType;
  };

  const getUserRole = (senderId) => {
    return participantRoles[senderId] || 'Member';
  };

  const getMessageSender = (message) => {
    return message.sender_name || 'Unknown User';
  };

  const getMessageAvatar = (message) => {
    return message.sender_profile_photo || userIcon;
  };

  const renderMultimediaTable = () => {
    return (
      <div className="multimedia-table-container">
        <div className="multimedia-header">
          <h3>Multimedia Files ({multimediaFiles.length})</h3>
        </div>
        <div className="multimedia-table">
          <div className="multimedia-table-header">
            <div className="header-cell">Name of member</div>
            <div className="header-cell">Role</div>
            <div className="header-cell">Type of file</div>
            <div className="header-cell">Date</div>
            <div className="header-cell">Actions</div>
          </div>
          <div className="multimedia-table-body">
            {multimediaFiles.length === 0 ? (
              <div className="no-multimedia">
                <p>No multimedia files found in this group.</p>
              </div>
            ) : (
              multimediaFiles.map((file) => (
                <div key={file.id} className="multimedia-row">
                  <div className="multimedia-cell">
                    <span className="sender-name">{file.sender_name || 'User name'}</span>
                  </div>
                  <div className="multimedia-cell">
                    <span className="user-role">{getUserRole(file.sender_id)}</span>
                  </div>
                  <div className="multimedia-cell">
                    <span className="file-type">{getFileTypeWithExtension(file.file_type, file.file_name, file.file_url)}</span>
                  </div>
                  <div className="multimedia-cell">
                    <span className="file-date">{formatDateTime(file.created_at)}</span>
                  </div>
                  <div className="multimedia-cell actions-cell">
                    <a 
                      href={file.file_url} 
                      className="view-action-btn"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      👁
                    </a>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="chat-history-group-wrapper">
        <div className="loading-state">
          <p>Loading chat history...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-history-group-wrapper">
      <div className="view-selector-container">
        <div className="custom-dropdown" ref={dropdownRef}>
          <div 
            className="dropdown-selected"
            onClick={() => setDropdownOpen(!dropdownOpen)}
          >
            <span>{viewType === 'chat' ? 'Chat' : 'Multimedia'}</span>
            <div className={`dropdown-arrow ${dropdownOpen ? 'open' : ''}`}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#20B2AA" strokeWidth="2">
                <polyline points="6,9 12,15 18,9"></polyline>
              </svg>
            </div>
          </div>
          {dropdownOpen && (
            <div className="dropdown-options">
              <div 
                className={`dropdown-option ${viewType === 'chat' ? 'selected' : ''}`}
                onClick={() => {
                  setViewType('chat');
                  setDropdownOpen(false);
                }}
              >
                Chat
              </div>
              <div 
                className={`dropdown-option ${viewType === 'multimedia' ? 'selected' : ''}`}
                onClick={() => {
                  setViewType('multimedia');
                  setDropdownOpen(false);
                }}
              >
                Multimedia
              </div>
            </div>
          )}
        </div>
      </div>
      
      {viewType === 'chat' ? (
        <div className="chat-view">
          <div className="chat-main-section">
            <div className="chat-messages-container" ref={chatContainerRef}>
              <div className="messages-list">
                {allMessages.length === 0 ? (
                  <div className="no-messages">
                    <p>No messages found in this group.</p>
                  </div>
                ) : (
                  visibleMessages.map((message, index) => {
                    const uniqueSenders = [...new Set(allMessages.map(m => m.sender_id))];
                    const senderIndex = uniqueSenders.indexOf(message.sender_id);
                    const messagePosition = senderIndex % 2 === 0 ? 'message-left' : 'message-right';
                    
                    return (
                      <div
                        key={message.id}
                        ref={el => messageRefs.current[message.id] = el}
                        className={`message-item ${messagePosition} ${selectedMessageId === message.id ? 'selected' : ''}`}
                      >
                        <div className="message-content">
                          {message.sender_name && (
                            <div className="message-header">
                              <span className="message-sender">{message.sender_name}</span>
                            </div>
                          )}
                          
                          {message.message && (
                            <div className="message-text">
                              {message.message}
                            </div>
                          )}
                          
                          {message.file_url && message.file_type && message.file_type.startsWith('image/') && (
                            <div className="message-image">
                              <img 
                                src={message.file_url} 
                                alt="Shared image"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            </div>
                          )}
                          
                          {message.file_url && message.file_type && !message.file_type.startsWith('image/') && (
                            <div className="message-file">
                              <a 
                                href={message.file_url} 
                                download={message.file_name || 'file'}
                                className="file-download"
                              >
                                📎 {message.file_name || 'Download File'}
                              </a>
                            </div>
                          )}
                          
                          <div className="message-time">
                            {formatDateTime(message.created_at)}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {loadingMore && (
                <div className="loading-more">
                  <p>Loading more messages...</p>
                </div>
              )}

              {!hasMoreMessages && visibleMessages.length > 0 && (
                <div className="end-of-messages">
                  <p>No more messages to load</p>
                </div>
              )}
            </div>
          </div>
          
          <div className="chat-search-section">
            <div className="search-container">
              <div className="search-input-container">
                <Input
                  type="text"
                  placeholder="Buscar en el historial de chat..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  icon={searchIcon}
                />
              </div>

              <div className="search-results">
                {searchTerm.trim() && searchResults.length > 0 && (
                  <div className="search-results-header">
                    <p>{searchResults.length} matches found</p>
                  </div>
                )}
                
                <div className="search-results-list">
                  {searchResults.map((result) => (
                    <div
                      key={result.id}
                      className="search-result-item"
                      onClick={() => scrollToMessage(result.id)}
                    >
                      <div className="result-avatar">
                        <img 
                          src={getMessageAvatar(result)} 
                          alt="User avatar"
                          onError={(e) => {
                            e.target.src = userIcon;
                          }}
                        />
                      </div>
                      <div className="result-content">
                        <div className="result-header">
                          <span className="result-sender">{getMessageSender(result)}</span>
                          <span className="result-time">{formatMessageTime(result.created_at)}</span>
                        </div>
                        <div 
                          className="result-text"
                          dangerouslySetInnerHTML={{ __html: result.highlightedContent }}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {searchTerm.trim() && searchResults.length === 0 && (
                  <div className="no-results">
                    <p>No matches found for "{searchTerm}"</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="multimedia-view">
          {renderMultimediaTable()}
        </div>
      )}
    </div>
  );
};

export default ChatHistoryGroup;